import { MenuItem, Select, Typography } from '@mui/material'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { BomItemController } from 'controllers/Project/BomItemController'
import { useAppController } from 'customHooks/useAppController'
import { isEqual } from 'lodash'
import { BomItemType } from 'model/Project/BoMItemRow'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  IssueCode,
  ResourceMatchType,
  ResourceSummaryDto,
  WorkingStepDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { useAppDispatch, useAppSelector } from 'store/configureStore'
import { makeStyles } from 'tss-react/mui'
import { useOrganizationAndProjectContext } from 'utils/useOrganizationContext'
import { useBomItemIssueDescription } from '../components/useBomItemIssueDescription'
import { saveBomItemResource } from '../store/asyncActions/saveBomItemResource'
import { ProjectEditableStateSelector } from '../store/selectors/ProjectEditableStateSelector'
import { bomItemActivitiesSelectors } from '../store/selectors/activities/bomItemActivitiesSelectors'
import { projectSelectors } from '../store/selectors/projectSelectors'

type Props = {
  bomItemPointers: BomItemPointer[]
  workingStep?: WorkingStepDto
  workingStepType?: WorkingStepType
  useStoreWorkingStep?: boolean
  onFocus?: () => void
  onBlur?: () => void
  disableLabel?: boolean
  disableUnderline?: boolean
  disableMatchDescription?: boolean
  disableSelectMaterialError?: boolean
  fullWidth?: boolean
  typographyClassname?: string
  /**
   * onClick propagation, default to false (e.stopPropagation() is called in the onClick handler)
   */
  enableClickPropagation?: boolean
  withLabel?: boolean
  disabled?: boolean
}

const useStyles = makeStyles({ name: 'ResourceSelector' })(() => {
  return {
    typographyDefault: {
      width: '100%',
      textAlign: 'left',
      maxWidth: '220px',
      whiteSpace: 'pre-wrap',
      display: 'inline-block',
    },
    typograophyFullWidth: {
      maxWidth: '100%',
    },
  }
})

export const WorkingStepResourceSelector = (props: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { classes, cx } = useStyles()
  const isBuyer = useAppSelector(projectSelectors.projectInBuyerViewSelector)

  const workingStepFromStore = useAppSelector(
    (props.useStoreWorkingStep &&
      bomItemActivitiesSelectors.byWorkingStep(
        props.bomItemPointers[0],
        props.workingStepType,
      )) ||
      (() => undefined),
    isEqual,
  )

  const workingStep: WorkingStepDto = props.useStoreWorkingStep
    ? workingStepFromStore || props.workingStep
    : props.workingStep

  const materialSummary = useAppSelector(
    bomItemActivitiesSelectors.articlesPerWorkingStep(
      props.bomItemPointers[0],
      workingStep?.secondaryWorkingStep ?? props.workingStepType,
    ),
    isEqual,
  )

  const [open, setOpen] = useState(false)

  const projectEditable = useAppSelector(ProjectEditableStateSelector)

  const { partyId, projectId } = useOrganizationAndProjectContext()
  const { controller, loading, error } = useAppController(
    () => new BomItemController(partyId, projectId),
  )

  const [resourceOptions, setResourceOptions] =
    useState<Array<ResourceSummaryDto>>()

  const handleChange = (e) => {
    dispatch(
      saveBomItemResource({
        bomItemPointers: props.bomItemPointers,
        workingStepType:
          workingStep?.secondaryWorkingStep ?? props.workingStepType,
        resource: resourceOptions.find((x) => x.id === e.target.value),
        controllerInstance: controller,
      }),
    )
  }

  const getHelperText = useCallback(() => {
    if (!workingStep?.['id']) {
      return null
    }

    let helperText =
      error[`get-selectable-resources-${props.bomItemPointers[0]}`]?.message

    if (
      props.bomItemPointers[0]?.type === BomItemType.partType &&
      !materialSummary?.id &&
      !props.disableSelectMaterialError &&
      !workingStep?.resource?.id
    ) {
      helperText = t(
        'project:select-material-before-selecting-resource',
        'material group or quality must be selected before selecting a resource',
      )
    }

    if (!helperText && !props.disableMatchDescription) {
      helperText = t(workingStep?.resource?.matchDescription)
    }

    return helperText
  }, [
    workingStep,
    error,
    props.bomItemPointers,
    props.disableSelectMaterialError,
    props.disableMatchDescription,
    materialSummary?.id,
    t,
  ])

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!props.enableClickPropagation) {
        e.stopPropagation()
      }

      if (
        props.disabled ||
        !projectEditable ||
        workingStep?.resource?.matchType ===
          ResourceMatchType.OneCapableResource
      ) {
        return
      }

      if (!open) {
        controller
          .GetBomItemSelectableResources(
            props.bomItemPointers[0],
            workingStep?.secondaryWorkingStep ?? props.workingStepType,
          )
          .then((summaries) => {
            setResourceOptions(
              summaries?.filter((x) => x.id !== workingStep?.resource?.id),
            )
          })
      }

      setOpen(!open)
    },
    [
      controller,
      open,
      projectEditable,
      props.bomItemPointers,
      props.enableClickPropagation,
      props.workingStepType,
      props.disabled,
      workingStep?.resource?.id,
      workingStep?.resource?.matchType,
      workingStep?.secondaryWorkingStep,
    ],
  )

  const { getIssueDescription } = useBomItemIssueDescription()
  const resourceNotSetIssue = useAppSelector(
    projectSelectors.bomItemIssueByCodeSelector(
      props.bomItemPointers?.[0],
      IssueCode.ResourceNotSet,
    ),
  )

  if (isBuyer) {
    return null
  }

  return (
    <span>
      {resourceNotSetIssue &&
        workingStep?.['id'] &&
        !workingStep?.resource?.id && (
          <Typography variant="caption" color="error">
            {getIssueDescription(resourceNotSetIssue)}
          </Typography>
        )}
      {workingStep?.resource?.id && (
        <>
          {props.withLabel && (
            <LocalizedTypography
              translationKey="project:resource"
              variant="caption"
              color="textSecondary"
            />
          )}
          <Select
            value={workingStep.resource?.id ?? ''}
            required
            onChange={handleChange}
            label={props.disableLabel ? null : t('project:resource')}
            open={open}
            fullWidth
            onClick={handleClick}
            onClose={() => {
              setOpen(false)
              setResourceOptions([])
            }}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            disableUnderline={props.disableUnderline}
            disabled={
              props.disabled ||
              workingStep?.resource?.matchType ===
                ResourceMatchType.OneCapableResource ||
              !projectEditable ||
              (workingStep?.resource?.id && !workingStep?.resource?.name)
            }
            MenuProps={{
              disablePortal: false,
              style: { zIndex: 10000 },
            }}
            renderValue={() => {
              if (
                workingStep?.resource?.id &&
                (!workingStep?.resource.name ||
                  workingStep?.resource.name === '')
              ) {
                return (
                  <LocalizedTypography
                    translationKey="project:resource-info-not-available"
                    variant="inherit"
                  >
                    resource info not available
                  </LocalizedTypography>
                )
              }

              return (
                <Typography
                  variant="inherit"
                  // variant={workingStep?.resource?.id ? 'caption' : 'caption'}
                  color={workingStep?.resource?.id ? 'textPrimary' : 'GrayText'}
                  className={cx(
                    classes.typographyDefault,
                    props.typographyClassname,
                    {
                      [classes.typograophyFullWidth]: props.fullWidth,
                    },
                  )}
                >
                  {workingStep?.resource?.name ||
                    t('project:select-resource', 'select a resource')}
                </Typography>
              )
            }}
          >
            {loading[
              `get-selectable-resources-${props.bomItemPointers[0]}-${workingStep.primaryWorkingStep}`
            ] && (
              <MenuItem value="">
                <Typography variant="caption">{`${t(
                  'common:loading',
                )}...`}</Typography>
              </MenuItem>
            )}
            {
              // this one is here to not have the @mui runtime warning
              // about current value not being in the options.
              // notice the display: none to not show it
              workingStep?.resource?.id &&
                workingStep.resource?.id !==
                  '00000000-0000-0000-0000-000000000000' && (
                  <MenuItem
                    value={workingStep.resource?.id}
                    style={{ display: 'none' }}
                    sx={{
                      borderBottom: (theme) =>
                        `2px solid ${theme.palette.divider}`,
                      gap: '8px',
                    }}
                  >
                    <Typography variant="caption">
                      {workingStep?.resource?.name}
                    </Typography>
                  </MenuItem>
                )
            }
            {resourceOptions
              ?.filter((x) => x.id !== workingStep?.resource?.id)
              .map((x) => {
                return (
                  <MenuItem key={x.id} value={x.id}>
                    <Typography variant="caption">{x.name}</Typography>
                  </MenuItem>
                )
              })}
          </Select>
        </>
      )}
      <Typography variant="caption" color="textSecondary">
        {getHelperText()}
      </Typography>
    </span>
  )
}
